import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STATUS_LABEL, useApp } from "@/lib/i18n";

const BASE = ["queued", "preparing_audio", "transcribing", "polishing", "generating_notes"];
const BOT = ["bot_joining", "bot_recording", "bot_processing"];
const RANGE = { queued: [0, 5], preparing_audio: [5, 15], transcribing: [15, 75], polishing: [75, 90], generating_notes: [90, 98] };

const percentFor = (status, progress) => {
  const r = RANGE[status];
  if (!r) return null;
  const [done, total] = (progress || "").split("/").map(Number);
  const frac = status === "transcribing" && total ? done / total : 0.3;
  return Math.round(r[0] + (r[1] - r[0]) * frac);
};

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const useElapsed = (since) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  return since ? Math.max(0, (now - new Date(since).getTime()) / 1000) : 0;
};

export const StripedBar = ({ value, testId }) => (
  <div className="h-4 w-full overflow-hidden rounded-full bg-violet-100" data-testid={testId}>
    <div className={`progress-stripes h-full rounded-full bg-violet-600 transition-[width] duration-700 ease-out ${value == null ? "w-1/3 progress-indeterminate" : ""}`} style={value == null ? undefined : { width: `${Math.max(3, value)}%` }} />
  </div>
);

export const ProcessingProgress = ({ status, error, onRetry, canWrite, progress, bot, since, duration }) => {
  const { L } = useApp();
  const steps = bot ? [...BOT, ...BASE] : BASE;
  const cur = status === "bot_scheduled" ? "bot_joining" : status;
  const idx = steps.indexOf(cur);
  const pct = percentFor(cur, progress);
  const elapsed = useElapsed(since);
  const estimate = duration ? Math.max(60, duration * 0.08) : null;
  const [done, total] = (progress || "").split("/");
  const failed = status === "failed";
  return (
    <div className={`rounded-3xl border-2 p-6 sm:p-8 ${failed ? "border-red-200 bg-red-50/40" : "border-violet-200 bg-gradient-to-b from-violet-50 to-white"}`} data-testid="processing-state">
      <div className="flex items-start gap-4">
        <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-2xl ${failed ? "bg-red-100 text-red-600" : "bg-violet-600 text-white shadow-lg shadow-violet-300"}`}>
          {failed ? <AlertTriangle size={26} /> : <Loader2 size={26} className="animate-spin" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-violet-700">{failed ? L("Pemprosesan gagal", "Processing failed") : L("Sedang diproses", "Processing")}</p>
          <h2 className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl" data-testid="processing-stage">
            {L(...(STATUS_LABEL[cur] || STATUS_LABEL.queued))}{cur === "transcribing" && total && <span className="font-mono text-violet-700" data-testid="transcribe-progress"> · {L("bahagian", "part")} {done}/{total}</span>}…
          </h2>
        </div>
        {pct != null && !failed && <span className="font-mono text-3xl font-extrabold text-violet-700 sm:text-4xl" data-testid="processing-percent">{pct}%</span>}
      </div>
      {!failed && <div className="mt-6"><StripedBar value={pct} testId="processing-bar" /></div>}
      {!failed && (
        <div className="mt-3 flex flex-wrap justify-between gap-2 text-xs text-slate-500">
          <span data-testid="processing-elapsed">{L("Masa berlalu", "Elapsed")}: <strong className="font-mono text-slate-700">{fmt(elapsed)}</strong></span>
          {estimate && <span data-testid="processing-eta">{L("Anggaran", "Estimate")}: ~{Math.ceil(estimate / 60)} {L("minit", "min")}</span>}
        </div>
      )}
      <ol className="mt-6 flex flex-wrap gap-2">
        {steps.map((s, i) => (
          <li key={s} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${failed ? "bg-slate-100 text-slate-400" : i < idx ? "bg-emerald-100 text-emerald-700" : i === idx ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-400"}`}>
            {i < idx && !failed ? <CheckCircle2 size={12} /> : i === idx && !failed ? <Loader2 size={12} className="animate-spin" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            {L(...STATUS_LABEL[s])}
          </li>
        ))}
      </ol>
      {failed ? (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-red-50 p-4 text-sm text-red-700" data-testid="meeting-error">
          <span className="flex items-center gap-2"><AlertTriangle size={16} /> {error}</span>
          {canWrite && <Button size="sm" variant="outline" className="rounded-xl" onClick={onRetry} data-testid="retry-job-btn"><RotateCcw size={14} className="mr-1" /> {L("Cuba semula", "Retry")}</Button>}
        </div>
      ) : (
        <p className="mt-5 text-sm text-slate-500">{L("Anda boleh meninggalkan halaman ini — kami akan berbunyi dan memberi notifikasi apabila siap.", "You can leave this page — we'll chime and notify you when it's done.")}</p>
      )}
    </div>
  );
};
